import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { useLocalize } from '@treams/treams-hooks/useLocalize';
import * as graphql from '@treams/treams-network';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { OptionList } from '@treams/treams-ui/common/OptionList';
import { PlainInput } from '@treams/treams-ui/common/PlainInput';
import { Radio } from '@treams/treams-ui/common/Radio';
import { tokens } from '@treams/treams-ui/theme/tokens';

import FilledInBy from './common/FilledInBy';
import RequiredToggle from './common/RequiredToggle';
import Title from './common/Title';

type LocalOption = {
	key: string;
	text: graphql.LocalizedString;
};

type FormValues = {
	title: graphql.LocalizedString;
	fillInBy: graphql.EvaluationFillInBy;
	optional: boolean;
	optionsMethod: graphql.EvaluationMultipleChoiceOptionsMethod;
	options: LocalOption[];
};

function hasText(value: graphql.LocalizedString | null | undefined) {
	return (value ?? []).some((entry) => entry.text.trim().length > 0);
}

function displayedOptionText(option: LocalOption, languageID: string) {
	const entry = option.text.find((item) => item.languageID === languageID);

	if (entry) {
		return entry.text;
	}

	return option.text.find((item) => item.text)?.text ?? '';
}

function replaceOptionText(options: LocalOption[], key: string, languageID: string, text: string) {
	return options.map((option) => {
		if (option.key !== key) {
			return option;
		}

		return {
			...option,
			text: [
				...option.text.filter((entry) => entry.languageID !== languageID),
				{ languageID, text },
			],
		};
	});
}

function trimmedText(value: graphql.LocalizedString) {
	return value
		.map((entry) => ({ languageID: entry.languageID, text: entry.text.trim() }))
		.filter((entry) => entry.text.length > 0);
}

function withKeys(options: graphql.EvaluationMultipleChoiceItem['options']): LocalOption[] {
	return options.map((option) => ({
		key: crypto.randomUUID(),
		text: option.text,
	}));
}

export default function MultipleChoiceModal(props: {
	fillInBy: graphql.EvaluationFillInBy;
	optional?: boolean | null;
	item: graphql.EvaluationMultipleChoiceItem;
	onChange: (
		item: graphql.EvaluationMultipleChoiceItem | undefined,
		fillInBy: graphql.EvaluationFillInBy,
		optional: boolean,
	) => void;
}) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const formik = useFormik<FormValues>({
		initialValues: {
			title: props.item.title,
			fillInBy: props.fillInBy,
			optional: props.optional ?? false,
			optionsMethod: props.item.optionsMethod,
			options: withKeys(props.item.options),
		},
		onSubmit: () => {},
	});

	const emitChange = (values: FormValues) => {
		const options = values.options
			.filter((option) => hasText(option.text))
			.map((option) => ({ text: trimmedText(option.text) }));
		const title = trimmedText(values.title);
		const valid = hasText(title) && options.length >= 2;

		props.onChange(
			valid
				? {
						title,
						optionsMethod: values.optionsMethod,
						options,
					}
				: undefined,
			values.fillInBy,
			values.optional,
		);
	};

	const setFieldValue: typeof formik.setFieldValue = (field, value, shouldValidate) => {
		emitChange({ ...formik.values, [field]: value });
		return formik.setFieldValue(field, value, shouldValidate);
	};

	const getFieldHelpers: typeof formik.getFieldHelpers = (name) => ({
		...formik.getFieldHelpers(name),
		setValue: (value, shouldValidate) => setFieldValue(name, value, shouldValidate),
	});

	return (
		<FormikProvider value={{ ...formik, setFieldValue, getFieldHelpers }}>
			<FlexGap p={3} flexDirection="column" gap={tokens.gap.section}>
				<Title label={t('common.settings.evaluationForms.multipleChoice.question.label')} />
				<FlexGap
					role="radiogroup"
					aria-label={t('common.settings.evaluationForms.multipleChoice.title')}
					flexDirection="column"
					gap={tokens.gap.element}
				>
					<Radio.Card
						title={t('common.settings.evaluationForms.multipleChoice.optionsMethod.single')}
						checked={
							formik.values.optionsMethod === graphql.EvaluationMultipleChoiceOptionsMethod.Single
						}
						onChange={() =>
							setFieldValue('optionsMethod', graphql.EvaluationMultipleChoiceOptionsMethod.Single)
						}
					/>
					<Radio.Card
						title={t('common.settings.evaluationForms.multipleChoice.optionsMethod.multiple')}
						checked={
							formik.values.optionsMethod === graphql.EvaluationMultipleChoiceOptionsMethod.Multiple
						}
						onChange={() =>
							setFieldValue('optionsMethod', graphql.EvaluationMultipleChoiceOptionsMethod.Multiple)
						}
					/>
				</FlexGap>
				<OptionList
					items={formik.values.options}
					active
					minItems={2}
					addLabel={t('common.settings.evaluationForms.multipleChoice.options.add')}
					createItem={() => ({ key: crypto.randomUUID(), text: [] })}
					onChange={(options) => setFieldValue('options', options)}
					renderInput={(option, inputProps) => {
						const text = displayedOptionText(option, language.id);
						const placeholder = t(
							'common.settings.evaluationForms.multipleChoice.option.placeholder',
						);

						return (
							<div ref={inputProps.registerRef}>
								<PlainInput
									multiline={false}
									value={text}
									placeholder={placeholder}
									label={text || placeholder}
									onChange={(next) =>
										setFieldValue(
											'options',
											replaceOptionText(formik.values.options, option.key, language.id, next),
										)
									}
									onFocus={inputProps.onFocus}
									onKeyDown={inputProps.onKeyDown}
									autoFocus={inputProps.autoFocus}
								/>
							</div>
						);
					}}
				/>
				<FilledInBy />
				<RequiredToggle />
			</FlexGap>
		</FormikProvider>
	);
}
